import React, { useEffect, useState } from "react";
import { Chip, CircularProgress, FormHelperText } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { useAutocompleteFetch } from "components/AsyncAutocomplete/hooks/useAutocompleteFetch";
import { Controller, useFormContext } from "react-hook-form";
import { SelectOptions, useDebounce } from "hooks";
import { FormControl, TextField } from "components";

interface AsyncAutocomplete {
    flex?: number;
    name: string;
    clearOnChangeOf?: string | number | boolean;
    multiple?: boolean;
    freeSolo?: boolean;
    disableMargin?: boolean;
    minLength?: number;
    isDisabled?: boolean;
    error?: FieldError;
    searchFn(queryName: string, searchValue: string): Promise<string[]>;
    defaultValue?: SelectOptions[];
    // eslint-disable-next-line
    onChange?(onChange: (...event: any[]) => void, option: SelectOptions[] | null): void;
}

export const AsyncAutocomplete: React.FC<AsyncAutocomplete> = ({
    flex = 1,
    name,
    minLength = 0,
    disableMargin = false,
    multiple = true,
    isDisabled = false,
    freeSolo = false,
    error,
    searchFn,
    defaultValue = [],
    onChange: onOptionChange,
}) => {
    const { control } = useFormContext();
    const [autocompleteText, setAutocompleteText] = useState("");
    const [isOpen, setOpen] = useState(false);
    const debouncedValue = useDebounce(autocompleteText);
    const { isLoading, options, clearOptions } = useAutocompleteFetch(name, searchFn, debouncedValue, minLength);

    // Clear options when value changes
    useEffect(() => {
        if (debouncedValue.length === 0 || !isOpen) {
            clearOptions();
        }
    }, [clearOptions, debouncedValue, isOpen]);

    // eslint-disable-next-line
    const handleValuesChange = (onChange: (...event: any[]) => void, values: any) => {
        // if autocomplete is set as multiple it always return value as array
        if (multiple) {
            const options = values.map((value: SelectOptions | string) => {
                if (typeof value === "string") {
                    return {
                        name: value,
                        value,
                    };
                }

                return value;
            });

            onOptionChange?.(onChange, options);

            return onChange(options);
        }

        onOptionChange?.(onChange, [values]);

        onChange(values);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        switch (event.key) {
            case "Tab": {
                if (autocompleteText.length > 0) {
                    control.setValue(name, [
                        ...control.getValues(name),
                        { name: autocompleteText, value: autocompleteText },
                    ]);
                }
                break;
            }

            default:
        }
    };

    return (
        <FormControl flex={flex} disableMargin={disableMargin}>
            <Controller
                name={name}
                defaultValue={multiple ? defaultValue : null}
                control={control}
                render={({ onChange, value }) => {
                    return (
                        <Autocomplete
                            value={value}
                            open={isOpen}
                            onChange={(event, val) => handleValuesChange(onChange, val)}
                            multiple={multiple}
                            disabled={isDisabled}
                            onOpen={() => setOpen(true)}
                            onClose={() => setOpen(false)}
                            getOptionSelected={(option, value) => option.name === value.name}
                            getOptionLabel={(option) => option.name}
                            freeSolo={freeSolo}
                            options={options}
                            loading={isLoading}
                            renderTags={(value, getTagProps) => {
                                return value.map((option, index: number) => {
                                    return <Chip key={index} label={option.name} {...getTagProps({ index })} />;
                                });
                            }}
                            renderInput={(params) => {
                                return (
                                    <TextField
                                        {...params}
                                        value={autocompleteText}
                                        error={!!error}
                                        onChange={(e) => setAutocompleteText(e.target.value)}
                                        label="Value"
                                        InputProps={{
                                            ...params.InputProps,
                                            onKeyDown: handleKeyDown,
                                            endAdornment: (
                                                <>
                                                    {isLoading && <CircularProgress color="inherit" size={20} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                );
                            }}
                        />
                    );
                }}
            />
            {error && <FormHelperText error={true}>{error.message}</FormHelperText>}
        </FormControl>
    );
};
